import React, { useEffect, useRef } from 'react';
import { useDispatch } from 'react-redux';
import { logoutAndWipeBrowser, getToken } from '../Utils/authUtils';

const INACTIVITY_LIMIT_MS = 2 * 60 * 60 * 1000;
const HIDDEN_AT_KEY = 'cardiac_tab_hidden_at';

/**
 * Enforces a per-tab timeout when the app is not visible. We deliberately use
 * Page Visibility rather than input-idle tracking: a clinician reading an open
 * report should not be logged out, while an abandoned background tab should.
 */
const TabInactivityLogout = () => {
  const dispatch = useDispatch();
  const loggingOut = useRef(false);

  useEffect(() => {
    const logoutAndReload = () => {
      if (loggingOut.current || !getToken()) return;
      loggingOut.current = true;
      sessionStorage.removeItem(HIDDEN_AT_KEY);
      void logoutAndWipeBrowser(dispatch);
    };

    const checkVisibility = () => {
      if (document.hidden) {
        sessionStorage.setItem(HIDDEN_AT_KEY, String(Date.now()));
        return;
      }

      const hiddenAt = Number(sessionStorage.getItem(HIDDEN_AT_KEY));
      sessionStorage.removeItem(HIDDEN_AT_KEY);
      if (hiddenAt && Date.now() - hiddenAt >= INACTIVITY_LIMIT_MS) {
        logoutAndReload();
      }
    };

    // Covers a tab restored from the browser's back/forward cache as well.
    checkVisibility();
    document.addEventListener('visibilitychange', checkVisibility);
    window.addEventListener('pageshow', checkVisibility);
    return () => {
      document.removeEventListener('visibilitychange', checkVisibility);
      window.removeEventListener('pageshow', checkVisibility);
    };
  }, [dispatch]);

  return null;
};

export default TabInactivityLogout;
